import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ProdHistory } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

interface PartProdHistTableProps {
  prodHistory: ProdHistory[];
}

export const PartProdHistTable: React.FC<PartProdHistTableProps> = ({
  prodHistory,
}) => {
  const { t } = useTranslation();

  const totalQty = prodHistory.reduce(
    (sum, record) => sum + record.qtyCompleted,
    0
  );

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-teal-50 px-4 py-3 border-b border-teal-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-teal-900">
          {t('parts.prodHistory')}
        </h3>
        <p className="text-xs text-teal-700">
          {prodHistory.length}{' '}
          {prodHistory.length === 1
            ? t('parts.records')
            : t('parts.recordPlural')}
          {totalQty !== 0 && ` • Total: ${totalQty.toLocaleString()}`}
        </p>
      </div>

      {/* Content */}
      {prodHistory.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.prodHistoryTable.closedDate')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.prodHistoryTable.rev')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.prodHistoryTable.job')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.prodHistoryTable.qty')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.prodHistoryTable.unitCost')}
                </th>
              </tr>
            </thead>
            <tbody>
              {prodHistory.map((record, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                    {formatDate(record.closedDate)}
                  </td>
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    {record.revisionNum}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/job-tracker?jobNum=${record.jobNum}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                    >
                      {record.jobNum}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-semibold">
                    {record.qtyCompleted.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">
                    ${record.unitCost.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="pb-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                {t('parts.prodHistoryTable.closedDate')}
              </p>
              <p className="text-sm font-medium text-gray-900">-</p>
            </div>
            <div className="pb-2 text-right">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                {t('parts.prodHistoryTable.qty')}
              </p>
              <p className="text-sm font-medium text-gray-900">0</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Format date string for display
 * Handles ISO format, timestamps, and various date formats
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try parsing as ISO date first
    if (dateString.includes('T') || dateString.includes('-')) {
      date = parseCalendarDate(dateString);
    } else if (/^\d+$/.test(dateString)) {
      // Handle timestamp in milliseconds
      date = new Date(parseInt(dateString, 10));
    } else {
      // Try direct Date constructor
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing failed
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}
